import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, TENANT_CONFIG } from '../utils/firebase-config';
import { useInactivityTimeout } from '../utils/useInactivityTimeout';
import { InactivityWarning } from '../components/InactivityWarning';
import { getTimeoutForRole, getWarningTimeForRole, INACTIVITY_CONFIG } from '../config/inactivity-config';

// Custom claims types
interface CustomClaims {
  admin?: boolean;
  role?: string;
  toolboxRole?: 'admin' | 'vss' | 'netzoom';
  tenant?: string;
  tenantId?: string;
  iShapesAdmin?: boolean;
  permissions?: string[];
  [key: string]: unknown;
}

interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  customClaims: CustomClaims | null;
  toolboxRole: 'admin' | 'vss' | 'netzoom' | null;
  userTenant: string | null;
  userUid: string | null;
  loginRole: 'admin' | 'vss' | 'netzoom' | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshClaims: () => Promise<void>;
  updateLoginRole: (role: 'admin' | 'vss' | 'netzoom' | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
  user: User; // Receive user from AppRoot (already authenticated)
}

export function AuthProvider({ children, user }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(user);
  const [isAdmin, setIsAdmin] = useState(false);
  const [customClaims, setCustomClaims] = useState<CustomClaims | null>(null);
  const [toolboxRole, setToolboxRole] = useState<'admin' | 'vss' | 'netzoom' | null>(null);
  const [userTenant, setUserTenant] = useState<string | null>(null);
  const [userUid, setUserUid] = useState<string | null>(null);
  const [loginRole, setLoginRole] = useState<'admin' | 'vss' | 'netzoom' | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  // Listen to Firebase auth state changes to get the real Firebase User object
  useEffect(() => {
    console.log('🔐 AuthContext: Setting up auth state listener...');
    console.log('  - Passed user prop:', user?.email, user?.uid);
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔥 Firebase auth state changed:', firebaseUser?.email || 'null');
      
      if (firebaseUser) {
        console.log('  - Firebase User email:', firebaseUser.email);
        console.log('  - Firebase User uid:', firebaseUser.uid);
        console.log('  - Has getIdTokenResult?', typeof firebaseUser.getIdTokenResult === 'function');
        
        setCurrentUser(firebaseUser);
        
        try {
          // Force refresh token to get latest custom claims
          console.log('  - Fetching ID token result...');
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          const claims = tokenResult.claims as CustomClaims;
          
          console.log('  - Custom claims loaded:', claims);
          console.log('  - toolboxRole:', claims.toolboxRole);
          console.log('  - admin:', claims.admin);
          
          setIsAdmin(!!claims.admin);
          setCustomClaims(claims);
          setToolboxRole(claims.toolboxRole || null);
          setUserTenant(claims.tenant || null);
          setUserUid(firebaseUser.uid);
          setLoginRole(claims.toolboxRole || null);
        } catch (error) {
          console.error('❌ Error loading custom claims:', error);
        }
      } else {
        console.warn('⚠️ No Firebase user - clearing state');
        setCurrentUser(null);
        setIsAdmin(false);
        setCustomClaims(null);
        setToolboxRole(null);
        setUserTenant(null);
        setUserUid(null);
        setLoginRole(null);
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [user]); // Re-run when user prop changes from AppRoot

  const updateLoginRole = (role: 'admin' | 'vss' | 'netzoom' | null) => {
    setLoginRole(role);
  };

  const refreshClaims = async () => {
    // Use auth.currentUser to ensure we have the actual Firebase User object
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      const tokenResult = await firebaseUser.getIdTokenResult(true);
      const claims = tokenResult.claims as CustomClaims;
      
      setCurrentUser(firebaseUser);
      setIsAdmin(!!claims.admin);
      setCustomClaims(claims);
      setToolboxRole(claims.toolboxRole || null);
      setUserTenant(claims.tenant || null);
      setUserUid(firebaseUser.uid);
      setLoginRole(claims.toolboxRole || null);
    }
  };

  const signIn = async (email: string, password: string) => {
    // State is already cleaned in AppRoot on mount, but we reset key values here for safety
    auth.tenantId = null; // Ensure clean start for tenant-based auth attempts
    
    try {
      console.log(`🔐 Attempting authentication for ${email}`);
      
      // Try main Firebase Auth first
      let userCredential;
      let authMethod = 'main';
      
      try {
        console.log('🔍 Trying main Firebase Auth...');
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Successfully signed in via main Firebase Auth');
      } catch (mainAuthError: any) {
        console.log('❌ Main auth failed:', mainAuthError.code);
        
        // If main auth fails, try tenant-specific authentication
        if (mainAuthError.code === 'auth/invalid-credential' || 
            mainAuthError.code === 'auth/user-not-found') {
          
          console.log('🔍 Trying netzoom tenant authentication...');
          
          // For tenant authentication, set tenant ID on the auth instance
          try {
            // Configure auth instance for netzoom tenant
            auth.tenantId = TENANT_CONFIG.NETZOOM;
            
            userCredential = await signInWithEmailAndPassword(auth, email, password);
            authMethod = 'netzoom-tenant';
            console.log('✅ Successfully signed in via netzoom tenant');
          } catch (tenantAuthError: any) {
            console.log('❌ Tenant auth also failed:', tenantAuthError.code);
            // Reset tenant ID back to null for next attempt
            auth.tenantId = null;
            throw mainAuthError; // Throw the original error
          }
        } else {
          // For other auth errors, reset tenant ID
          auth.tenantId = null;
          throw mainAuthError;
        }
      }
      
      // Force refresh token to get latest custom claims
      const tokenResult = await userCredential.user.getIdTokenResult(true);
      const claims = tokenResult.claims as CustomClaims;
      
      console.log('🎟️ Retrieved custom claims:', claims);
      console.log(`🏢 Authentication method: ${authMethod}`);
      
      setIsAdmin(!!claims.admin);
      setCustomClaims(claims);
      setToolboxRole(claims.toolboxRole || null);
      setUserTenant((claims as any).tenant || (claims as any).tenantId || authMethod);
      setUserUid(userCredential.user.uid);
      setLoginRole(claims.toolboxRole || null);
      
      console.log(`✅ Authentication successful for ${email} via ${authMethod}`);
      
    } catch (error: unknown) {
      console.error('🚨 Sign in error:', error);
      // Reset tenant ID on failure so next attempt starts fresh
      auth.tenantId = null;
      throw new Error(error instanceof Error ? error.message : 'Failed to sign in');
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      auth.tenantId = null; // Reset tenant ID to allow fresh login
      setIsAdmin(false);
      setCustomClaims(null);
      setToolboxRole(null);
      setUserTenant(null);
      setUserUid(null);
      setLoginRole(null);
      setShowInactivityWarning(false); // Hide warning on manual signout
      
      // Force complete state cleanup and page reload to unmount all components
      // This prevents stale React state and router context from causing errors
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    } catch (error: unknown) {
      console.error('Sign out error:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to sign out');
    }
  };

  // Enable inactivity timeout only when user is authenticated
  // Use role-based timeout for enhanced security
  const timeoutMinutes = getTimeoutForRole(toolboxRole || undefined);
  const warningMinutes = getWarningTimeForRole(toolboxRole || undefined);
  const warningSeconds = warningMinutes * 60;

  const { resetTimer } = useInactivityTimeout({
    timeoutMinutes,
    warningMinutes,
    enabled: INACTIVITY_CONFIG.ENABLED && !!currentUser,  // Only enable when user is logged in
    onWarning: () => {
      if (INACTIVITY_CONFIG.DEBUG_LOGGING) {
        console.log('🔔 Showing inactivity warning to user');
        console.log(`⏰ User role: ${toolboxRole}, timeout: ${timeoutMinutes}min, warning: ${warningMinutes}min`);
      }
      setShowInactivityWarning(true);
    },
    onTimeout: () => {
      if (INACTIVITY_CONFIG.DEBUG_LOGGING) {
        console.log('⏰ User timed out due to inactivity');
      }
      setShowInactivityWarning(false);
    }
  });

  const handleContinueSession = () => {
    if (INACTIVITY_CONFIG.DEBUG_LOGGING) {
      console.log('✅ User continued session - resetting inactivity timer');
    }
    setShowInactivityWarning(false);
    resetTimer();
  };

  const value: AuthContextType = {
    currentUser,
    isAdmin,
    customClaims,
    toolboxRole,
    userTenant,
    userUid,
    loginRole,
    loading,
    signIn,
    signOut,
    refreshClaims,
    updateLoginRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      {currentUser && (
        <InactivityWarning
          isVisible={showInactivityWarning}
          onContinue={handleContinueSession}
          onSignOut={signOut}
          warningTimeSeconds={warningSeconds}
        />
      )}
    </AuthContext.Provider>
  );
}
